import React, { FC, ReactNode } from 'react';
import { Trash2 } from 'react-feather';

import { Add, Refresh, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

export const managementControlSx = {
  height: 40,
  borderRadius: 2,
  textTransform: 'none',
};

export const managementTableSx = {
  border: '1px solid #e2e8f0',
  borderRadius: 3,
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
  overflowX: 'auto',
  backgroundColor: '#fff',
  '& .MuiTableRow-root': {
    transition: 'background-color 120ms ease',
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover': {
    backgroundColor: '#f8fafc',
  },
  '& .MuiTableCell-body': {
    color: '#334155',
    borderColor: '#e8edf4',
    py: 1.5,
  },
  '& .MuiTablePagination-root': {
    borderTop: '1px solid #e8edf4',
  },
};

interface ManagementPageHeaderProps {
  title: string;
  subtitle: string;
  actionLabel?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  secondaryAction?: ReactNode;
}

export const ManagementPageHeader: FC<ManagementPageHeaderProps> = ({
  title,
  subtitle,
  actionLabel,
  onAction,
  actionDisabled,
  secondaryAction,
}) => (
  <Stack
    direction={{ xs: 'column', md: 'row' }}
    alignItems={{ xs: 'stretch', md: 'flex-end' }}
    justifyContent="space-between"
    gap={2}
    sx={{ mb: 2.5 }}
  >
    <Box>
      <Typography
        component="h1"
        sx={{ fontSize: 28, fontWeight: 700, color: '#0f172a', mb: 0.5 }}
      >
        {title}
      </Typography>
      <Typography sx={{ color: '#64748b' }}>{subtitle}</Typography>
    </Box>
    <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
      {secondaryAction}
      {actionLabel && onAction && (
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={onAction}
          disabled={actionDisabled}
          sx={{
            ...managementControlSx,
            bgcolor: '#0b8fca',
            fontWeight: 700,
            '&:hover': { bgcolor: '#087caf' },
          }}
        >
          {actionLabel}
        </Button>
      )}
    </Stack>
  </Stack>
);

export const ManagementToolbar: FC<{ children: ReactNode }> = ({
  children,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      alignItems: { xs: 'stretch', md: 'center' },
      justifyContent: 'space-between',
      gap: 1.5,
      p: 2,
      mb: 2,
      bgcolor: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: 3,
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
    }}
  >
    {children}
  </Box>
);

interface ManagementSearchProps {
  value: string;
  placeholder: string;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

export const ManagementSearch: FC<ManagementSearchProps> = ({
  value,
  placeholder,
  onChange,
}) => (
  <TextField
    placeholder={placeholder}
    size="small"
    value={value}
    onChange={onChange}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <Search fontSize="small" />
        </InputAdornment>
      ),
    }}
    sx={{
      width: { xs: '100%', md: 360 },
      '& .MuiOutlinedInput-root': { height: 40, borderRadius: 2 },
    }}
  />
);

export const RefreshAction: FC<{ onClick: () => void }> = ({ onClick }) => (
  <Tooltip title="Refresh list">
    <IconButton
      aria-label="Refresh list"
      onClick={onClick}
      sx={{
        width: 40,
        height: 40,
        border: '1px solid #cbd5e1',
        borderRadius: 2,
        color: '#475569',
      }}
    >
      <Refresh fontSize="small" />
    </IconButton>
  </Tooltip>
);

interface BulkSelectionBarProps {
  count: number;
  singularLabel: string;
  pluralLabel: string;
  onClear: () => void;
  onDelete: () => void;
  disabled?: boolean;
}

export const BulkSelectionBar: FC<BulkSelectionBarProps> = ({
  count,
  singularLabel,
  pluralLabel,
  onClear,
  onDelete,
  disabled,
}) => {
  if (!count) return null;

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      justifyContent="space-between"
      gap={1.5}
      sx={{
        px: 2,
        py: 1,
        mb: 1.5,
        bgcolor: '#f8fbff',
        border: '1px solid #bfdbfe',
        borderRadius: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography sx={{ color: '#1e3a8a', fontWeight: 700 }}>
          {count} {count === 1 ? singularLabel : pluralLabel} selected
        </Typography>
        <Button onClick={onClear} sx={{ textTransform: 'none' }}>
          Clear selection
        </Button>
      </Stack>
      <Button
        variant="outlined"
        color="error"
        startIcon={<Trash2 size={16} />}
        onClick={onDelete}
        disabled={disabled}
        sx={{ height: 40, borderRadius: 2, textTransform: 'none' }}
      >
        Delete selected
      </Button>
    </Stack>
  );
};
